/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/naming-convention */
import { isViewAsCompany, isViewAsPartner } from "../../core/storage/local-storage.util";
import { Company } from "../../modules/portal/companies/company.type";
import { Partner } from "../../modules/portal/partners/partner.type";

export type UserType = "global" | "partner" | "company";
export type UserRight = "admin" | "manager" | "viewer";

export enum UserTypes {
  Global = "global",
  Partner = "partner",
  Company = "company",
}

export enum UserRights {
  Admin = "admin",
  Manager = "manager",
  Viewer = "viewer",
}

interface IUserInfo {
  id: string;
  firstName: string;
  role: string;
  type: UserType;
  right: UserRight;
  accessToken: string;
  companyId?: string | undefined | null;
  partnerId?: string | undefined | null;

  hasType(types: UserType[]): boolean;
  hasRight(rights: UserRight[]): boolean;

  isGlobal(): boolean;
  isPartner(): boolean;
  isPartnerOrViewAsPartner(): boolean;
  isCompany(): boolean;
  isCompanyOrViewAsCompany(): boolean;
  isGlobalAdmin(): boolean;
}

export class UserInfo implements IUserInfo {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  type: UserType;
  right: UserRight;
  isCWRep: boolean;
  isSalesRep: boolean;
  accessToken: string;
  refreshToken: string;
  companyId: string | undefined | null;
  partnerId: string | undefined | null;
  company: Company | undefined | null;
  selectedCompany: Company | undefined | null;
  partner: Partner | undefined | null;
  email: string;
  selectedCompanyId: string | undefined | null;
  companies: Company[] | undefined | null;
  lastLoginAt: string;

  constructor(values: IUserInfo) {
    Object.entries(values).forEach(([k, v]) => {
      this[k] = v;
    });
  }

  hasType(types: UserType[]): boolean {
    return types.includes(this.type);
  }

  hasRight(rights: UserRight[]): boolean {
    return rights.includes(this.right);
  }

  isGlobal(): boolean {
    return this.type === UserTypes.Global;
  }

  isPartner(strict?: boolean): boolean {
    return this.type === UserTypes.Partner && (strict ? !this.isSalesRep : true);
  }

  isPartnerOrViewAsPartner(right?: UserRight[]): boolean {
    if (right?.length) {
      if (!this.hasRight(right)) {
        return false;
      }
    }

    if (this.isPartner()) {
      return true;
    }

    return isViewAsPartner();
  }

  isCompany(): boolean {
    return this.type === UserTypes.Company;
  }

  /**
   *
   * @param options.right user right
   * @param options.ignoreMultiCompanies ignore condition if company user has more than 1 company. Default: false
   * @returns true or false
   */
  isCompanyOrViewAsCompany(options?: { right?: UserRight[]; ignoreIfMultiCompanies: boolean }): boolean {
    if (options?.right?.length) {
      if (!this.hasRight(options.right)) {
        return false;
      }
    }

    if (this.isCompany()) {
      if (options?.ignoreIfMultiCompanies && this.hasManyCompanies()) {
        return false;
      }
      return true;
    }

    return isViewAsCompany();
  }

  isGlobalAdmin(): boolean {
    return this.isGlobal() && this.hasRight(["admin"]);
  }

  isGlobalManager(): boolean {
    return this.isGlobal() && this.hasRight(["manager"]);
  }

  isGlobalAdminOrManager(): boolean {
    return this.isGlobal() && this.hasRight(["admin", "manager"]);
  }

  isPartnerAdmin(): boolean {
    return this.isPartner() && this.hasRight(["admin"]);
  }

  isPartnerManager(): boolean {
    return this.isPartner() && this.hasRight(["manager"]);
  }

  isPartnerAdminOrManager(): boolean {
    return this.isPartner() && this.hasRight(["admin", "manager"]);
  }

  isCompanyAdmin(): boolean {
    return this.isCompany() && this.hasRight(["admin"]);
  }

  isCompanyManager(): boolean {
    return this.isCompany() && this.hasRight(["manager"]);
  }

  isCompanyAdminOrManager(): boolean {
    return this.isCompany() && this.hasRight(["admin", "manager"]);
  }

  isCompanyViewer(): boolean {
    return this.isCompany() && this.hasRight(["viewer"]);
  }

  isSalesRepUser(): boolean {
    return this.type === UserTypes.Partner && this.hasRight(["manager"]) && this.isSalesRep;
  }

  isCWRepUser(): boolean {
    return this.isGlobalManager() && this.isCWRep;
  }

  canCreateUser(): boolean {
    return (
      (this.isGlobal() && this.hasRight(["admin", "manager"])) ||
      this.isPartnerAdminOrManager() ||
      this.isCompanyAdmin()
    );
  }

  viewAsSelector(): boolean {
    return this.isGlobal() || this.isPartner() || (this.isCompany() && this.companies.length > 0);
  }

  canResetAccount(): boolean {
    return (this.isGlobal() && this.hasRight(["admin", "manager"])) || this.isPartnerAdmin();
  }

  hasManyCompanies(): boolean {
    return this.companies.length > 0;
  }

  fullName(): string {
    return `${this.firstName} ${this.lastName}`;
  }
}
